'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { usePathname } from 'next/navigation';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { GTM_EVENT, GTM_MANUAL_PUSH_ATTR } from '@/shared/constants/gtm';
import { pushGTMEvent, toBtnName } from '@/shared/utils/gtm';
import { isWithinSchedule, resolvePopupSchedule, type PopupSchedule } from '@/shared/utils/popupSchedule';

export type ExitIntentPopupEntry = PopupSchedule & {
  id: string;
  paths?: string[];
  label: string;
  href: string;
  bgImage: string;
  imageAlt: string;
  className?: string;
  /** CTA. type 기본 black. outline이면 채움은 그대로 두고 보더 추가 (black gray-600 / white gray-400) */
  cta?: {
    type?: 'black' | 'white';
    outline?: boolean;
  };
  /** 닫기 버튼. 기본 black */
  close?: {
    type?: 'black' | 'white';
  };
};

export type ExitIntentConfig = {
  excludeExact?: string[];
  excludeContains?: string[];
  include?: ExitIntentPopupEntry[];
  default: ExitIntentPopupEntry;
};

type ExitIntentContextValue = {
  popup: ExitIntentPopupEntry | null;
  dismiss: () => void;
};

const ExitIntentContext = createContext<ExitIntentContextValue | null>(null);

export function useExitIntentPopup() {
  return useContext(ExitIntentContext)?.popup ?? null;
}

export function useDismissExitIntent() {
  return useContext(ExitIntentContext)?.dismiss ?? (() => {});
}

function isEntryWithinSchedule(entry: ExitIntentPopupEntry): boolean {
  return isWithinSchedule(resolvePopupSchedule(entry));
}

function resolvePopup(config: ExitIntentConfig, pathname: string): ExitIntentPopupEntry | null {
  if (config.excludeExact?.some((p) => pathname === p || pathname.startsWith(p + '/'))) {
    return null;
  }
  if (config.excludeContains?.some((word) => pathname.includes(word))) {
    return null;
  }
  const pathMatchedInclude = config.include?.find((entry) =>
    entry.paths?.some((p) => pathname === p || pathname.startsWith(p + '/')),
  );
  if (pathMatchedInclude) {
    return isEntryWithinSchedule(pathMatchedInclude) ? pathMatchedInclude : null;
  }
  return isEntryWithinSchedule(config.default) ? config.default : null;
}

const STORAGE_KEY = 'exit_intent_dismissed_until';

function getNextKSTMidnightMs(): number {
  const KST_OFFSET_MS = 9 * 60 * 60 * 1000;
  const nowKST = new Date(Date.now() + KST_OFFSET_MS);
  return (
    Date.UTC(nowKST.getUTCFullYear(), nowKST.getUTCMonth(), nowKST.getUTCDate() + 1) -
    KST_OFFSET_MS
  );
}

function persistDismissUntilMidnight() {
  localStorage.setItem(STORAGE_KEY, String(getNextKSTMidnightMs()));
}

type Props = {
  config: ExitIntentConfig;
  children: React.ReactNode;
};

export function ExitIntentDialog({ config, children }: Props) {
  const pathname = usePathname();
  const popup = resolvePopup(config, pathname);
  const [openedOnPathname, setOpenedOnPathname] = useState<string | null>(null);
  // mouseleave 는 팝업이 떠 있는 동안에도 계속 발사되므로, 경로당 한 번만 노출로 집계한다.
  const viewedPathnameRef = useRef<string | null>(null);

  // 다른 페이지로 이동하면 열린 팝업 상태 초기화 (effect 없이 pathname 동기화)
  if (openedOnPathname !== null && openedOnPathname !== pathname) {
    setOpenedOnPathname(null);
  }

  const isOpen = openedOnPathname === pathname;

  const dismiss = useCallback(() => {
    persistDismissUntilMidnight();
    setOpenedOnPathname(null);
  }, []);

  useEffect(() => {
    if (!popup) return;

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY > 0) return;
      const dismissedUntil = Number(localStorage.getItem(STORAGE_KEY) ?? 0);
      if (Date.now() < dismissedUntil) return;
      setOpenedOnPathname(pathname);
      persistDismissUntilMidnight();

      if (viewedPathnameRef.current === pathname) return;
      viewedPathnameRef.current = pathname;
      pushGTMEvent(GTM_EVENT.EXIT_POPUP_VIEW, {
        btnname: toBtnName(popup.id),
      });
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [popup, pathname]);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      persistDismissUntilMidnight();
      setOpenedOnPathname(null);
      return;
    }
    setOpenedOnPathname(pathname);
  };

  return (
    <ExitIntentContext.Provider value={{ popup, dismiss }}>
      <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay
            className="fixed inset-0 z-1000 cursor-pointer bg-black/40"
            onClick={dismiss}
          />
          <Dialog.Content
            // 클릭이 아닌 '노출' 시점 이벤트라 자동 수집을 끈다.
            // 실제 push 는 위 mouseleave 핸들러가 담당.
            data-event={GTM_EVENT.EXIT_POPUP_VIEW}
            data-btnname={popup ? toBtnName(popup.id) : undefined}
            {...{ [GTM_MANUAL_PUSH_ATTR]: '' }}
            className="fixed left-1/2 top-1/2 z-1001 -translate-x-1/2 -translate-y-1/2"
          >
            <Dialog.Title className="sr-only">이탈 방지 팝업</Dialog.Title>
            <Dialog.Description className="sr-only">
              페이지를 떠나기 전 확인해주세요.
            </Dialog.Description>
            {children}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </ExitIntentContext.Provider>
  );
}
